/**
 * Uygulama geneli tarih-saat biçimlendirici (tek kaynak — her yerde kullanılır). Sabit **sayısal** biçim,
 * dile bağımsız: tarih **"GG.AA.YYYY"**, saat **"SS:DD"** (24s), ikisi birlikte **"GG.AA.YYYY SS:DD"**.
 * Her tarih/saat gösterimi bu fonksiyonlardan geçer.
 */

/**
 * Uygulama dili (i18n: 'tr'/'en') → BCP-47 Intl locale (tek kaynak). İngilizce için **en-GB** (gün-önce)
 * seçilir. PrimeReact Calendar vb. Intl locale'e ihtiyaç duyan yerler bunu çağırır.
 */
export function intlLocale(lang: string): string {
  return lang.startsWith('tr') ? 'tr-TR' : 'en-GB'
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** ISO/Date string → "GG.AA.YYYY" (dilden bağımsız sabit). Boş/geçersizde "—". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`
}

/** ISO/Date string → "SS:DD" (24 saat). Boş/geçersizde "—". */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/** ISO/Date string → "GG.AA.YYYY SS:DD" (dilden bağımsız sabit; uygulama geneli). Boş/geçersizde "—". */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${formatDate(iso)} ${formatTime(iso)}`
}

/**
 * ISO/Date string → **kurumsal saat diliminde** (Europe/Istanbul) "GG.AA.YYYY SS:DD" — tarayıcının
 * saat diliminden **bağımsız**. Boş/geçersizde "—".
 *
 * <p>{@link formatDateTime}'dan ayrı olmasının sebebi: o, damgayı **tarayıcının** yerel saatinde
 * gösterir ve bu, kullanıcının kendi anını okuduğu yerlerde (gönderim/okunma zamanı listeleri) doğru
 * olandır. Bu fonksiyon ise sunucunun **kural gereği** ürettiği bir anı gösterir: ör. sessiz saat
 * ötelemesinin hedefi olan gönderim saati. UTC'ye ayarlı bir istemcide `formatDateTime` bunu
 * "05:00" yazar ve mesaj kendi söylediği pencerenin **içinde** bir saat vaat ederek kendini çürütür —
 * kuralın dilimi neyse gösterim de o dilimde olmalıdır ({@link businessToday} ile aynı gerekçe).
 *
 * <p>Dilim koda gömülüdür; backend'de ayarlanabilir (`app.time.zone`) → kurulum başka bir ülkeye
 * taşınırsa iki yer **birlikte** değiştirilir (bkz. {@link businessDayFormatter}).
 */
export function formatBusinessDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  try {
    // 'en-GB' → "12/08/2026, 08:00" (gün önce, 24s); ayraçlar sabit biçime çevrilir.
    const parts = businessDateTimeFormatter().formatToParts(d)
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? ''
    return `${get('day')}.${get('month')}.${get('year')} ${get('hour')}:${get('minute')}`
  } catch {
    // Intl tanımadığı bir timeZone için RangeError fırlatır (beklenmez); tek bir damga tüm ekranı
    // düşürmesin diye yerel biçime düşülür — bkz. businessToday'deki aynı savunma.
    return formatDateTime(iso)
  }
}

/** {@link formatBusinessDateTime}'ın tembel kurulan biçimlendiricisi ({@link businessDayFormatter} deseni). */
let businessDateTimeFormatterInstance: Intl.DateTimeFormat | null = null
function businessDateTimeFormatter(): Intl.DateTimeFormat {
  businessDateTimeFormatterInstance ??= new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return businessDateTimeFormatterInstance
}

/**
 * "YYYY-MM-DD" form değeri → yerel `Date` (takvim bileşeni Date ile çalışır). Zaman diliminden bağımsız
 * olması için parçalanıp yerel gün olarak kurulur (UTC ayrıştırma gün kaymasına yol açabilir). Boş/geçersizde null.
 */
export function parseISODate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

/** `Date` → "YYYY-MM-DD" form değeri (yerel gün; saatsiz). Boş değerde "". */
export function toISODate(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return ''
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * <b>Kurumsal takvim günü</b> (Europe/Istanbul) — tarayıcının saat diliminden <b>bağımsız</b>.
 *
 * <p>Terminler Türkiye takvim tarihidir ve iş Türkiye'de yürür; backend de "bugün"ü bu dilimden okur
 * (`ClockConfig` → `Europe/Istanbul`, 2026-08-05'te UTC'den çevrildi). Tarayıcının yerel günü
 * kullanılırsa iki taraf farklı güne bakar: ör. UTC'ye ayarlı bir istemcide 11.08 22:30 (= İstanbul
 * 12.08 01:30) anında termin 11.08 olan bir aksiyon ekranda "gecikmemiş", uçta "gecikmiş" görünür —
 * buton aktifken istek 400 döner (ya da tersi).
 *
 * <p>Dönen değer, kurumsal günün <b>yerel gece yarısı</b> olarak kurulmuş hâlidir; böylece
 * {@link parseISODate} çıktısıyla (o da yerel gün kurar) doğrudan karşılaştırılabilir ve takvim
 * bileşenlerine `minDate` olarak verilebilir.
 */
export function businessToday(): Date {
  try {
    // 'en-CA' sabit "YYYY-MM-DD" üretir (dile bağlı değil) → parçalanıp yerel gün olarak kurulur.
    const iso = businessDayFormatter().format(new Date())
    const day = parseISODate(iso)
    if (day) return day
  } catch {
    // Intl, tanımadığı bir timeZone için RangeError fırlatır. Beklenmez (IANA dilimleri tüm güncel
    // tarayıcılarda var) ama bir tarih yardımcısının tüm ekranı düşürmesi kabul edilemez → yerel güne düş.
  }
  const local = new Date()
  local.setHours(0, 0, 0, 0)
  return local
}

/**
 * Kurumsal gün biçimlendiricisi — <b>modül kapsamında tembel kurulur</b>: {@link businessToday} her
 * render'da birkaç kez çağrılır ({@code deadlineMinDate}, gecikme karşılaştırması, takvim alt sınırı) ve
 * {@code Intl.DateTimeFormat} örneği durumsuz olduğundan her seferinde yenisini kurmak gereksizdir.
 *
 * <p><b>Dilim koda gömülüdür.</b> Backend'de ayarlanabilir ({@code app.time.zone} /
 * {@code BusinessTimeProperties}); kurulum başka bir ülkeye taşınırsa <b>iki yer birlikte</b>
 * değiştirilmelidir, aksi hâlde ekran ile uç yeniden farklı güne bakar.
 */
let businessDayFormatterInstance: Intl.DateTimeFormat | null = null
function businessDayFormatter(): Intl.DateTimeFormat {
  businessDayFormatterInstance ??= new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return businessDayFormatterInstance
}

/**
 * Bugün, gece yarısı (saatsiz) — takvim `minDate` alt sınırı ve gecikme karşılaştırması için tek kaynak.
 *
 * <p><b>Kurumsal güne</b> ({@link businessToday}) delege eder: backend'in tüm tarih kuralları
 * (termin alt sınırı, gecikme, Gecikti-çıkış termini) Europe/Istanbul gününe göre çalışır, dolayısıyla
 * ekranın "bugün"ü de aynı günü göstermelidir.
 */
export function todayStart(): Date {
  return businessToday()
}

/**
 * Termin (deadline) takvimi alt sınırı: <b>bugünden önce seçilemez</b>. Toplantı tarihi <b>gelecekteyse</b>
 * termin toplantıdan önce olamaz → toplantı tarihi döner; toplantı bugünde/geçmişte ya da toplantı yoksa
 * bugün döner. Yani alt sınır = <b>max(bugün, toplantı tarihi)</b> ve asla bugünden geriye inmez (geçmiş termin
 * engellenir). Not: mevcut (ör. gecikmiş) bir aksiyonun geçmiş termini alanda görünmeye devam eder; bu sınır
 * yalnız <b>yeni seçimi</b> kısıtlar.
 */
export function deadlineMinDate(meetingDateIso?: string | null): Date {
  const today = todayStart()
  const meeting = parseISODate(meetingDateIso)
  return meeting && meeting > today ? meeting : today
}
